<template>
    <v-dialog
        v-model="isOpen"
        width="90vw"
        max-width="1000"
        persistent
        :retain-focus="false"
        :fullscreen="$vuetify.breakpoint.xs"
    >
        <v-card class="multicart-confirm-modal">
            <v-card-title class="warning white--text d-flex align-center">
                <v-icon left color="white">mdi-cart-outline</v-icon>
                Artikel in Warenkorbregal einlagern
                <v-spacer></v-spacer>
                <v-btn icon dark @click="closeModal">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>
            
            <v-card-text class="pa-2 pa-md-3 modal-content-wrapper">
                <!-- Scanning Mode View -->
                <div v-if="scanningMode" class="scanning-mode">
                    <!-- Two-column layout for tablet -->
                    <div v-if="currentScanItem" class="wizard-layout">
                        <!-- Left column: Image -->
                        <div class="wizard-image-section">
                            <div class="wizard-image-container">
                                <v-img
                                    v-if="getImageUrl(currentScanItem)"
                                    :src="getImageUrl(currentScanItem)"
                                    class="wizard-product-image"
                                    contain
                                >
                                    <template v-slot:placeholder>
                                        <v-row class="fill-height ma-0" align="center" justify="center">
                                            <v-progress-circular indeterminate color="warning" size="32"></v-progress-circular>
                                        </v-row>
                                    </template>
                                </v-img>
                                <div v-else class="wizard-image-placeholder">
                                    <v-icon size="64" color="grey lighten-1">mdi-package-variant</v-icon>
                                </div>
                            </div>
                        </div>

                        <!-- Right column: Product info and actions -->
                        <div class="wizard-info-section">
                            <div class="info-card-content">
                                <!-- Progress counter -->
                                <div class="text-center mb-3">
                                    <div class="text-h4 font-weight-bold primary--text">
                                        {{ scanProgress.current }} / {{ scanProgress.total }}
                                    </div>
                                    <div class="text-body-2 grey--text">
                                        Artikel
                                    </div>
                                </div>

                                <v-divider class="mb-3"></v-divider>

                                <!-- Product info -->
                                <div class="text-center mb-2">
                                    <h2 class="text-h5 mb-2 font-weight-bold">{{ currentScanItem.sku }}</h2>
                                    <v-chip color="warning" small>
                                        <v-icon left x-small>mdi-numeric</v-icon>
                                        Menge: {{ currentScanItem.qty || currentScanItem.vqty }}
                                    </v-chip>
                                </div>

                                <!-- Confirmation instruction -->
                                <div class="text-center my-3">
                                    <div class="text-h6 font-weight-bold primary--text">
                                        Label auf Artikel geklebt?
                                    </div>
                                </div>

                                <!-- Action buttons (visible on tablet) -->
                                <div class="wizard-actions-inline mt-auto">
                                    <v-btn
                                        v-if="currentItemIndex < allSelectedItems.length"
                                        @click="confirmCurrentItem"
                                        color="warning"
                                        :loading="loading"
                                        :disabled="loading"
                                        block
                                        x-large
                                    >
                                        <v-icon left small>mdi-check-bold</v-icon>
                                        Bestätigen
                                    </v-btn>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Article selection (initial mode) -->
                <div v-else class="initial-selection-layout">
                    <!-- Left side: Product grid -->
                    <div class="selection-products-section">
                        <!-- Current packlist items -->
                        <div class="mb-4">
                            <h3 class="text-subtitle-1 mb-3 d-flex align-center">
                                <v-icon left small color="primary">mdi-package-variant-closed</v-icon>
                                Artikel der aktuellen Packliste
                            </h3>

                            <!-- Product Grid -->
                            <div v-if="displayedCurrentItems.length > 0" class="product-grid-compact">
                                <div
                                    v-for="(item, index) in displayedCurrentItems"
                                    :key="`current-${getItemUniqueId(item)}-${index}`"
                                    class="product-item-compact"
                                    :class="{
                                        'selected': isItemSelected(item, 'current'),
                                        'paired-item': isPairedItem(item) && !isInOriginalPacklist(item)
                                    }"
                                    @click="toggleCurrentItem(item)"
                                >
                                    <div class="product-image-container-compact">
                                        <v-img
                                            v-if="getImageUrl(item)"
                                            :src="getImageUrl(item)"
                                            class="product-image"
                                            contain
                                        >
                                            <template v-slot:placeholder>
                                                <v-row class="fill-height ma-0" align="center" justify="center">
                                                    <v-progress-circular indeterminate color="grey lighten-1" size="24"></v-progress-circular>
                                                </v-row>
                                            </template>
                                        </v-img>
                                        <div v-else class="product-image-placeholder">
                                            <v-icon color="grey lighten-1">mdi-package-variant</v-icon>
                                        </div>
                                    </div>

                                    <div class="product-info-compact">
                                        <div class="product-sku-compact">{{ item.sku }}</div>
                                        <v-chip x-small color="primary">
                                            Qty: {{ item.qty || item.vqty }}
                                        </v-chip>

                                        <!-- Paired indicator badge -->
                                        <v-chip
                                            v-if="isPairedItem(item) && !isInOriginalPacklist(item)"
                                            x-small
                                            color="warning"
                                            class="mt-1"
                                        >
                                            <v-icon x-small left>mdi-link</v-icon>
                                            Gepaart
                                        </v-chip>
                                    </div>

                                    <div class="product-checkbox-compact">
                                        <v-icon v-if="isItemSelected(item, 'current')" color="success">
                                            mdi-check-circle
                                        </v-icon>
                                        <v-icon v-else color="grey lighten-1">
                                            mdi-checkbox-blank-circle-outline
                                        </v-icon>
                                    </div>
                                </div>
                            </div>

                            <v-alert v-else type="info" outlined dense>
                                <div class="d-flex align-center justify-center py-2">
                                    <v-icon left small>mdi-information</v-icon>
                                    <div class="body-2">Keine Artikel der aktuellen Packliste verfügbar</div>
                                </div>
                            </v-alert>
                        </div>
                    </div>

                    <!-- Right side: Other items and actions -->
                    <div class="selection-actions-section">
                        <!-- WKL Printer Warning -->
                        <v-alert
                            v-if="!hasValidWklPrinter"
                            type="error"
                            dense
                            class="mb-4"
                        >
                            <span class="text-caption">
                                Kein WKL-Drucker konfiguriert. Barcode-Etiketten können nicht gedruckt werden.
                            </span>
                        </v-alert>

                        <!-- Other available items -->
                        <div v-if="otherAvailableItemsWithIndex.length > 0" class="mb-4">
                            <h3 class="text-subtitle-1 mb-3 d-flex align-center">
                                <v-icon left small color="secondary">mdi-archive</v-icon>
                                Weitere Artikel
                                <v-chip color="info" x-small class="ml-2">
                                    {{ otherAvailableItemsWithIndex.length }}
                                </v-chip>
                            </h3>

                            <v-select
                                v-model="selectedOtherItems"
                                :items="otherAvailableItemsWithIndex"
                                item-text="displayText"
                                item-value="uniqueId"
                                return-object
                                multiple
                                chips
                                deletable-chips
                                label="Weitere Artikel auswählen"
                                hide-details="auto"
                                outlined
                                dense
                                color="warning"
                                class="other-items-select"
                            >
                                <template v-slot:selection="{ item, index }">
                                    <v-chip
                                        v-if="index < 3"
                                        :key="item.uniqueId"
                                        x-small
                                        color="warning"
                                        text-color="white"
                                        close
                                        @click:close="removeOtherItem(item)"
                                    >
                                        {{ item.displayText }} ({{ item.qty }})
                                    </v-chip>
                                    <span
                                        v-if="index === 3"
                                        class="grey--text text-caption ml-1"
                                    >
                                        (+{{ selectedOtherItems.length - 3 }})
                                    </span>
                                </template>
                                <template v-slot:item="{ item }">
                                    <v-list-item-content>
                                        <v-list-item-title class="body-2">{{ item.displayText }}</v-list-item-title>
                                        <v-list-item-subtitle class="caption">Menge: {{ item.qty }}</v-list-item-subtitle>
                                    </v-list-item-content>
                                </template>
                            </v-select>
                        </div>

                        <!-- Summary and actions (visible on tablet) -->
                        <div class="selection-actions-inline">
                            <v-card outlined class="pa-3">
                                <v-btn
                                    @click="handleStartClick"
                                    color="warning"
                                    :loading="loading"
                                    :disabled="totalSelectedItems === 0 || !hasValidWklPrinter"
                                    block
                                    x-large
                                >
                                    <v-icon left>mdi-printer</v-icon>
                                    Labeldruck starten
                                </v-btn>
                                <v-btn @click="closeModal" text color="grey darken-1" block class="mt-2">
                                    Abbrechen
                                </v-btn>
                            </v-card>
                        </div>
                    </div>
                </div>
            </v-card-text>
            
            <!-- Mobile bottom actions for wizard mode -->
            <div v-if="scanningMode" class="wizard-actions-mobile pa-3">
                <v-btn
                    v-if="currentItemIndex < allSelectedItems.length"
                    @click="confirmCurrentItem"
                    color="warning"
                    :loading="loading"
                    :disabled="loading"
                    block
                    x-large
                >
                    <v-icon left>mdi-check-bold</v-icon>
                    Bestätigen
                </v-btn>
            </div>

            <v-divider v-if="!scanningMode" class="d-md-none"></v-divider>

            <!-- Bottom actions for initial mode (mobile only) -->
            <v-card-actions class="pa-3 initial-actions-mobile" v-if="!scanningMode">
                <!-- Initial selection actions -->
                    <v-spacer></v-spacer>
                    <v-btn @click="closeModal" text color="grey darken-1">
                        Abbrechen
                    </v-btn>
                    <v-btn
                        @click="handleStartClick"
                        color="warning"
                        :loading="loading"
                        :disabled="totalSelectedItems === 0 || !hasValidWklPrinter"
                    >
                        <v-icon left>mdi-printer</v-icon>
                        Labeldruck starten
                    </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import PacklistService from '@/services/PacklistService.js';

export default {
    name: 'MulticartConfirmModal',
    props: {
        multicartPayload: {
            type: Object,
            default: null
        },
        currentPacklistItems: {
            type: Array,
            default: () => []
        },
        currentPacklistName: {
            type: String,
            default: null
        },
        currentOrder: {
            type: Object,
            default: null
        },
        currentOrderId: {
            type: String,
            default: null
        },
        currentPrinter: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            isOpen: true,
            loading: false,
            selectedCurrentItems: [],
            selectedOtherItems: [],
            allowedToPair: ['wmf', 'amefa'],
            pairingSkus: [
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_WALDFREUNDE'],
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_BAUERNFREUNDE']
            ],
            skuPairing: [
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_FUSSBALLFREUNDE'],
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_WALDFREUNDE'],
                ['LYTO_BRETTCHEN', 'LYTO_AMEFA_BAUERNFREUNDE'],
            ],
            // Wizard workflow state
            scanningMode: false, // Keeping name for compatibility, but now it's a confirmation wizard
            currentItemIndex: 0,
            confirmedItems: [], // Track which items were actually confirmed (not skipped)
            activeOperationOrderId: null // Lock active operation to specific order - prevents race conditions
        }
    },
    computed: {
        hasValidWklPrinter() {
            return this.currentPrinter && this.currentPrinter.wkl && this.currentPrinter.wkl !== '';
        },
        isPairingAllowed() {
            // Check if the packlist name contains one of the allowed strings
            if (!this.currentPacklistName) {
                return false; // If no packlist name, disable pairing
            }
            const packlistNameLower = this.currentPacklistName.toLowerCase();
            return this.allowedToPair.some(allowed => packlistNameLower.includes(allowed));
        },
        filteredCurrentItems() {
            if (!this.multicartPayload || !this.currentOrder) {
                return [];
            }

            console.log('currentPacklistItems:', this.currentPacklistItems);
            console.log('currentPacklistItems SKUs:', this.currentPacklistItems.map(x => x.sku));
            
            // Include items that are part of the current packlist
            const itemsInsidePacklist = this.currentOrder.products.filter(product => {
                // Check if this SKU is part of any pairing (only if pairing is allowed)
                const isPartOfPairing = this.isPairingAllowed && this.skuPairing.some(pair =>
                    pair.includes(product.sku)
                );

                // For non-paired items, check if they're in currentPacklistItems
                const isInCurrentPacklist = this.currentPacklistItems.some(x => x.sku === product.sku);

                // Include if: (part of pairing AND pairing allowed) OR (in current packlist AND has meaningful attributes)
                return (isPartOfPairing || isInCurrentPacklist) &&
                       product.isMultiCart === true &&
                       product.status !== 'missing' &&
                       !product.shelf &&
                       product.attributes &&
                       Object.keys(product.attributes).length > 0;
            });

            console.log('itemsInsidePacklist before pairing', itemsInsidePacklist);

            // Check if SKUs should be paired together (only if pairing is allowed)
            const pairedItems = [];

            if (this.isPairingAllowed) {
                // For each item in the filtered list, check if it should be paired with other SKUs
                itemsInsidePacklist.forEach(item => {
                    this.skuPairing.forEach(pair => {
                        if (pair.includes(item.sku)) {
                            // Find all other SKUs in this pairing
                            const otherSkus = pair.filter(sku => sku !== item.sku);

                            // Find products in currentOrder that match the paired SKUs
                            otherSkus.forEach(pairedSku => {
                                const pairedProduct = this.currentOrder.products.find(product =>
                                    product.sku === pairedSku &&
                                    product.status !== 'missing' &&
                                    !product.shelf &&
                                    product.attributes &&
                                    Object.keys(product.attributes).length > 0
                                );

                                if (pairedProduct && !pairedItems.some(p => p.sku === pairedProduct.sku)) {
                                    pairedItems.push(pairedProduct);
                                }
                            });
                        }
                    });
                });
            }
            
            // Add paired items to the result (avoiding duplicates)
            pairedItems.forEach(pairedItem => {
                if (!itemsInsidePacklist.some(item => item.sku === pairedItem.sku)) {
                    itemsInsidePacklist.push(pairedItem);
                }
            });

            console.log('itemsInsidePacklist after pairing', itemsInsidePacklist);
            
            return itemsInsidePacklist;
        },
        otherAvailableItems() {
            if (!this.multicartPayload || !this.currentOrder) {
                return [];
            }

            const otherAvailableItems = this.currentOrder.products.filter(product => {
                return !this.currentPacklistItems.some(x => x.sku === product.sku) &&
                       product.status !== 'missing' &&
                       !product.shelf &&
                       product.attributes &&
                       Object.keys(product.attributes).length > 0;
            });

            return otherAvailableItems;
        },
        otherAvailableItemsWithIndex() {
            const skuCounts = {};
            const skuCounters = {};

            // Count occurrences of each SKU
            this.otherAvailableItems.forEach(item => {
                skuCounts[item.sku] = (skuCounts[item.sku] || 0) + 1;
            });

            // Add unique IDs and display text for duplicates
            return this.otherAvailableItems.map((item, index) => {
                const count = skuCounts[item.sku];
                if (count > 1) {
                    // Multiple items with same SKU - add counter
                    if (!skuCounters[item.sku]) {
                        skuCounters[item.sku] = 0;
                    }
                    skuCounters[item.sku]++;
                    return {
                        ...item,
                        displayText: `${item.sku} (#${skuCounters[item.sku]})`,
                        uniqueId: `${item.sku}-${index}`
                    };
                } else {
                    // Single item with this SKU
                    return {
                        ...item,
                        displayText: item.sku,
                        uniqueId: `${item.sku}-${index}`
                    };
                }
            });
        },
        displayedCurrentItems() {
            // Start with filtered current items
            const combined = [...this.filteredCurrentItems];

            // Add paired items from selectedOtherItems (only if pairing is allowed)
            if (this.isPairingAllowed) {
                this.selectedOtherItems.forEach(selectedItem => {
                    const isPairedItem = this.pairingSkus.some(pairingGroup =>
                        pairingGroup.includes(selectedItem.sku)
                    );

                    if (isPairedItem && selectedItem.uniqueId) {
                        // Extract index from uniqueId (format: "SKU-index")
                        const indexStr = selectedItem.uniqueId.split('-').pop();
                        const originalIndex = parseInt(indexStr, 10);

                        if (!isNaN(originalIndex) && this.otherAvailableItems[originalIndex]) {
                            const originalProduct = this.otherAvailableItems[originalIndex];
                            const productId = this.getItemUniqueId(originalProduct);
                            // Add if not already in combined list
                            if (!combined.some(item => this.getItemUniqueId(item) === productId)) {
                                combined.push(originalProduct);
                            }
                        }
                    }
                });
            }

            return combined;
        },
        totalSelectedItems() {
            return this.selectedCurrentItems.length + this.selectedOtherItems.length;
        },
        allSelectedItems() {
            return [...this.selectedCurrentItems, ...this.selectedOtherItems];
        },
        currentScanItem() {
            if (!this.scanningMode || this.currentItemIndex >= this.allSelectedItems.length) {
                return null;
            }
            return this.allSelectedItems[this.currentItemIndex];
        },
        scanProgress() {
            return {
                current: this.currentItemIndex + 1,
                total: this.allSelectedItems.length
            };
        }
    },
    methods: {
        closeModal() {
            this.isOpen = false;
            this.$emit('close-modal');
        },
        getItemUniqueId(item) {
            // Create unique ID using designId if available
            const designId = item.designId || item.attributes?.designId;
            if (designId) {
                return `${item.sku}-${designId}`;
            }

            // For items without designId, use translated.line1 to differentiate engravings
            const line1 = item.translated?.line1;
            if (line1) {
                return `${item.sku}-${line1}`;
            }

            // Fallback to just SKU (should rarely happen)
            return item.sku;
        },
        toggleCurrentItem(item) {
            const itemId = this.getItemUniqueId(item);
            const index = this.selectedCurrentItems.findIndex(selected =>
                this.getItemUniqueId(selected) === itemId
            );

            if (index > -1) {
                this.selectedCurrentItems.splice(index, 1);
            } else {
                this.selectedCurrentItems.push(item);
            }
        },
        isItemSelected(item, type) {
            if (type === 'current') {
                const itemId = this.getItemUniqueId(item);
                return this.selectedCurrentItems.some(selected =>
                    this.getItemUniqueId(selected) === itemId
                );
            }
            return false;
        },
        removeOtherItem(item) {
            const itemId = this.getItemUniqueId(item);
            const index = this.selectedOtherItems.findIndex(selected =>
                this.getItemUniqueId(selected) === itemId
            );
            if (index > -1) {
                this.selectedOtherItems.splice(index, 1);
            }
        },

        handleStartClick() {
            console.log('🔴 BUTTON CLICKED - Starting wizard!');
            console.log('🔴 totalSelectedItems:', this.totalSelectedItems);
            this.startWizardWorkflow();
        },

        async startWizardWorkflow() {
            console.log('🔵 [WIZARD START] Starting wizard workflow');

            this.loading = true;

            // CRITICAL: Capture order ID at the start to prevent race conditions
            const capturedOrderId = this.currentOrderId;

            if (!capturedOrderId) {
                console.error('❌ [WIZARD ERROR] No order ID available');
                alert('Keine Bestellung ausgewählt.');
                this.loading = false;
                return;
            }

            try {
                console.log(`📦 [WIZARD INIT] Starting wizard for Order: ${capturedOrderId}`);
                console.log(`📦 Selected items: ${this.allSelectedItems.length}`);
                console.log(`📦 Printer: ${this.multicartPayload?.printer}`);
                console.log(`📦 Slot: ${this.multicartPayload?.location}`);

                // Just get/assign slot, no label printing yet
                console.log(`📍 [WIZARD SLOT] Requesting multicart slot assignment (Order: ${capturedOrderId})`);
                const response = await PacklistService.requestMultiCartSlot(
                    capturedOrderId, // Use captured value - NOT this.currentOrderId
                    {
                        items: [] // Empty - we'll print labels one by one
                    }
                );

                // CRITICAL: Validate order hasn't changed during async operation
                if (this.currentOrderId !== capturedOrderId) {
                    console.error(`❌ [RACE CONDITION] Order changed during wizard initialization! Expected: ${capturedOrderId}, Got: ${this.currentOrderId}`);
                    throw new Error('Order switched during wizard initialization');
                }

                console.log(`✅ [WIZARD SLOT] Response received (Order: ${capturedOrderId})`);

                if (response.status === 200) {
                    // Update slot location
                    if (response.data.location) {
                        this.multicartPayload.location = response.data.location;
                        console.log(`📍 [WIZARD SLOT] Slot assigned: ${response.data.location} (Order: ${capturedOrderId})`);
                    }

                    // Switch to wizard mode
                    this.scanningMode = true;
                    this.currentItemIndex = 0;
                    this.confirmedItems = [];

                    console.log(`🔄 [WIZARD MODE] Switched to wizard mode, showing first item (Order: ${capturedOrderId})`);

                    // Validate one more time before printing
                    if (this.currentOrderId !== capturedOrderId) {
                        console.error(`❌ [RACE CONDITION] Order changed before initial print! Expected: ${capturedOrderId}, Got: ${this.currentOrderId}`);
                        throw new Error('Order switched before initial label print');
                    }

                    // Print labels for the FIRST item
                    console.log(`🖨️ [WIZARD PRINT] Printing initial label (Order: ${capturedOrderId})`);
                    await this.printLabelsForCurrentItem();
                }
            } catch (error) {
                console.error(`❌ [WIZARD ERROR] Error in wizard workflow (Order: ${capturedOrderId}):`, error);
                console.error('❌ Error details:', error.response?.data);
                alert('Fehler beim Starten des Workflows. Bitte versuchen Sie es erneut.');
            } finally {
                this.loading = false;
            }
        },

        async printLabelsForCurrentItem() {
            const item = this.currentScanItem;
            // CRITICAL: Capture order ID immediately to prevent race conditions
            const capturedOrderId = this.currentOrderId;
            const capturedLocation = this.multicartPayload?.location;
            const capturedPrinter = this.currentPrinter?.wkl;

            if (!item || !capturedOrderId) {
                console.warn('⚠️ No current item or order ID');
                return;
            }

            try {
                console.log(`🖨️ [PRINT START] Printing label for ${item.sku} (Order: ${capturedOrderId}, Location: ${capturedLocation})`);

                // Print barcode label for this one item
                await PacklistService.printMultiCartShelfLabel(
                    capturedOrderId, // Use captured value - NOT this.currentOrderId
                    {
                        location: capturedLocation,
                        items: [item],
                        wklPrinter: capturedPrinter
                    }
                );

                // CRITICAL: Validate order hasn't changed during async operation
                if (this.currentOrderId !== capturedOrderId) {
                    console.error(`❌ [RACE CONDITION] Order changed during print! Expected: ${capturedOrderId}, Got: ${this.currentOrderId}`);
                    throw new Error('Order switched during label printing');
                }

                console.log(`✅ [PRINT SUCCESS] Label printed for ${item.sku} (Order: ${capturedOrderId})`);
            } catch (error) {
                console.error(`❌ [PRINT ERROR] Failed for ${item.sku}:`, error);
                alert('Fehler beim Drucken des Etiketts. Bitte versuchen Sie es erneut.');
                throw error;
            }
        },

        async addItemToShelf(item) {
            try {
                // CRITICAL: Capture order ID and order object immediately to prevent race conditions
                const capturedOrderId = this.currentOrderId;
                const capturedOrder = this.currentOrder;

                if (!capturedOrderId || !capturedOrder) {
                    console.error('❌ [SHELF ERROR] Missing order data');
                    return false;
                }

                console.log(`📋 [SHELF START] Adding ${item.sku} to shelf (Order: ${capturedOrderId})`);

                const itemDesignId = item.designId || item.attributes?.designId;

                // Get packlist item IDs - using CAPTURED order object
                const packlistItemIds = capturedOrder.packlistItems
                    ? capturedOrder.packlistItems
                        .filter(packlistItem => {
                            // Prefer matching by designId if both have it
                            if (itemDesignId && packlistItem.designId) {
                                return packlistItem.designId === itemDesignId;
                            }
                            // Fall back to SKU matching if designId is not available
                            return packlistItem.sku === item.sku;
                        })
                        .map(packlistItem => packlistItem.packlistItemId)
                    : [];

                console.log(`📋 Found ${packlistItemIds.length} packlist IDs for ${item.sku} (Order: ${capturedOrderId}, DesignId: ${itemDesignId || 'N/A'})`);

                // Call putMultiCartSlot to add this item to the shelf
                const payload = {
                    location: this.multicartPayload?.location,
                    items: [item],
                    packlistItemIds: packlistItemIds
                };

                const response = await PacklistService.putMultiCartItems(
                    capturedOrderId, // Use captured value - NOT this.currentOrderId
                    payload
                );

                // CRITICAL: Validate order hasn't changed during async operation
                if (this.currentOrderId !== capturedOrderId) {
                    console.error(`❌ [RACE CONDITION] Order changed during shelf placement! Expected: ${capturedOrderId}, Got: ${this.currentOrderId}`);
                    throw new Error('Order switched during shelf operation');
                }

                if (response.status === 200) {
                    console.log(`✅ [SHELF SUCCESS] Item ${item.sku} added to shelf (Order: ${capturedOrderId})`);
                    return true;
                }
            } catch (error) {
                console.error('❌ [SHELF ERROR] Failed to add item to shelf:', error);
                alert('Fehler beim Hinzufügen zum Regal. Bitte versuchen Sie es erneut.');
                return false;
            }
        },

        async confirmCurrentItem() {
            this.loading = true;

            // CRITICAL: Lock to current order to prevent parent from switching orders
            this.activeOperationOrderId = this.currentOrderId;
            console.log(`🔒 [OPERATION LOCK] Operation locked to Order: ${this.activeOperationOrderId}`);

            try {
                const item = this.currentScanItem;
                console.log(`✅ [CONFIRM START] User confirmed label for: ${item.sku} (Locked to Order: ${this.activeOperationOrderId})`);

                // Add this item to the shelf
                const success = await this.addItemToShelf(item);

                if (success) {
                    // Track this item as confirmed
                    this.confirmedItems.push(item);
                    console.log(`📝 [CONFIRM PROGRESS] Item confirmed. Total: ${this.confirmedItems.length}/${this.allSelectedItems.length} (Order: ${this.activeOperationOrderId})`);

                    // Move to next item
                    this.currentItemIndex++;

                    // If all items confirmed, complete the wizard
                    if (this.currentItemIndex >= this.allSelectedItems.length) {
                        console.log(`✅ [CONFIRM COMPLETE] All items confirmed! Completing wizard (Order: ${this.activeOperationOrderId})`);
                        this.completeWizard();
                    } else {
                        // Print labels for the NEXT item
                        console.log(`🖨️ [CONFIRM NEXT] Printing labels for next item (Order: ${this.activeOperationOrderId})`);
                        await this.printLabelsForCurrentItem();
                    }
                }
            } catch (error) {
                console.error(`❌ [CONFIRM ERROR] Error confirming item (Order: ${this.activeOperationOrderId}):`, error);
                alert('Fehler beim Bestätigen des Artikels.');
            } finally {
                this.loading = false;
                // CRITICAL: Unlock operation when complete
                console.log(`🔓 [OPERATION UNLOCK] Operation unlocked (Was: ${this.activeOperationOrderId})`);
                this.activeOperationOrderId = null;
            }
        },

        async skipCurrentItem() {
            // Skip current item without confirming
            console.log('⏭️ Skipping item:', this.currentScanItem?.sku);
            this.currentItemIndex++;

            // If all items processed, complete the wizard
            if (this.currentItemIndex >= this.allSelectedItems.length) {
                this.completeWizard();
            } else {
                // Print labels for the NEXT item
                console.log('🖨️ Printing labels for next item...');
                try {
                    await this.printLabelsForCurrentItem();
                } catch (error) {
                    console.error('❌ Error printing labels after skip:', error);
                }
            }
        },

        async completeWizard() {
            // All items processed, labels already printed one by one
            console.log('✅ Wizard complete!');
            console.log(`📊 Total selected: ${this.allSelectedItems.length}, Total confirmed: ${this.confirmedItems.length}`);

            this.closeModal();

            // Emit event to parent to refresh and navigate to next item
            this.$emit('scanning-completed');
        },

        async confirmStorage() {
            console.log('🟢 confirmStorage called');
            // Legacy method - kept for backward compatibility
            // Now redirects to wizard workflow
            await this.startWizardWorkflow();
        },
        resetSelections() {
            this.selectedCurrentItems = [];
            this.selectedOtherItems = [];
        },
        resetModalState() {
            // Reset all modal state to initial values
            this.selectedCurrentItems = [];
            this.selectedOtherItems = [];
            this.scanningMode = false;
            this.currentItemIndex = 0;
            this.confirmedItems = [];
            this.loading = false;
            console.log('🔄 Modal state fully reset');
        },
        preselectCurrentPacklistItems() {
            // Pre-select all items from the current order (they are already filtered by orderId)
            this.selectedCurrentItems = [...this.filteredCurrentItems];
        },
        preselectPairedItems() {
            // Only preselect paired items if pairing is allowed
            if (!this.isPairingAllowed) {
                return;
            }

            // Check each pairing group
            for (const pairingGroup of this.pairingSkus) {
                const skuCounts = {};

                // Count available quantity for each SKU in the pairing
                pairingGroup.forEach(sku => {
                    const count = this.currentOrder.products.filter(product =>
                        product.sku === sku &&
                        product.isMultiCart === true &&
                        product.status !== 'missing' &&
                        !product.shelf
                    ).length;
                    skuCounts[sku] = count;
                });

                // Check if ALL SKUs in this pairing group are available
                const allSkusAvailable = pairingGroup.every(sku => skuCounts[sku] > 0);

                if (allSkusAvailable) {
                    // Auto-select ALL matching paired products
                    pairingGroup.forEach(sku => {
                        // Find all products with this SKU
                        const matchingProducts = this.currentOrder.products.filter(product =>
                            product.sku === sku &&
                            product.isMultiCart === true &&
                            product.status !== 'missing' &&
                            !product.shelf
                        );

                        // Add each matching product
                        matchingProducts.forEach(product => {
                            const productId = this.getItemUniqueId(product);

                            // Check if it's in filteredCurrentItems (auto-selected)
                            const isInFiltered = this.filteredCurrentItems.some(item =>
                                this.getItemUniqueId(item) === productId
                            );

                            if (isInFiltered) {
                                // Already added via filteredCurrentItems
                                if (!this.selectedCurrentItems.some(item =>
                                    this.getItemUniqueId(item) === productId
                                )) {
                                    this.selectedCurrentItems.push(product);
                                }
                            } else {
                                // Need to add to selectedOtherItems
                                // Find in otherAvailableItemsWithIndex
                                const itemWithIndex = this.otherAvailableItemsWithIndex.find(item =>
                                    this.getItemUniqueId(item) === productId &&
                                    !this.selectedOtherItems.some(selected => selected.uniqueId === item.uniqueId)
                                );
                                if (itemWithIndex && !this.selectedOtherItems.some(item => item.uniqueId === itemWithIndex.uniqueId)) {
                                    this.selectedOtherItems.push(itemWithIndex);
                                }
                            }
                        });
                    });
                }
            }
        },
        isPairedItem(item) {
            // Check if this item's SKU is part of any pairing group
            return this.pairingSkus.some(pairingGroup =>
                pairingGroup.includes(item.sku)
            );
        },
        isInOriginalPacklist(item) {
            // Check if this item is in the original packlist (not added via pairing)
            return this.currentPacklistItems.some(packlistItem => packlistItem.sku === item.sku);
        },
        getImageUrl(product) {
            if (!product.attributes || typeof product.attributes !== 'object') {
                return null;
            }

            let hasDesignId = product.designId || product.attributes.designId;
            let hasPreview = product.attributes.preview;

            if (hasDesignId) {
                return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
            } else if (hasPreview) {
                // Handle both string and array formats
                if (typeof hasPreview === 'string') {
                    return hasPreview;
                } else if (Array.isArray(hasPreview) && hasPreview.length > 0) {
                    return hasPreview[0].url || hasPreview[0];
                }
            } else {
                // Search for any http URL in attributes
                for (const key in product.attributes) {
                    const value = product.attributes[key];
                    if (typeof value === 'string' && value.startsWith('http')) {
                        return value;
                    }
                }
            }

            return null;
        }
    },
    watch: {
        isOpen(newVal) {
            if (!newVal) {
                this.$emit('close-modal');
            } else {
                // Reset ALL state when modal opens
                this.resetModalState();
                // Pre-select current packlist items and paired items
                this.$nextTick(() => {
                    this.preselectCurrentPacklistItems();
                    this.preselectPairedItems();
                });
            }
        },
        currentOrderId(newOrderId, oldOrderId) {
            // CRITICAL: Block order switches during active operations
            if (this.activeOperationOrderId) {
                console.error(`🚫 [RACE PREVENTION] Cannot switch orders during active operation! Locked to: ${this.activeOperationOrderId}, Attempted: ${newOrderId}`);
                // This will be caught by our async operation validation
                // The parent should not switch orders while modal has active operations
                return;
            }

            // Reset state when order changes (packlist switch)
            if (newOrderId !== oldOrderId && newOrderId) {
                console.log(`🔄 [ORDER SWITCH] Order changed, resetting modal state: ${oldOrderId} → ${newOrderId}`);
                this.resetModalState();
            }
        },
        filteredCurrentItems: {
            handler() {
                // Update preselection when items are loaded
                this.$nextTick(() => {
                    this.preselectCurrentPacklistItems();
                    this.preselectPairedItems();
                });
            },
            immediate: true
        }
    },
    mounted() {
        console.log('🎬 MulticartConfirmModal mounted');
        console.log('📦 Props:', {
            multicartPayload: this.multicartPayload,
            currentOrderId: this.currentOrderId,
            currentPacklistItems: this.currentPacklistItems?.length
        });

        // Pre-select current packlist items and paired items when component mounts
        this.$nextTick(() => {
            this.preselectCurrentPacklistItems();
            this.preselectPairedItems();
        });
    }
}
</script>

<style scoped>
.multicart-confirm-modal {
    border-radius: 8px;
    overflow: hidden;
}

/* Modal Content Wrapper */
.modal-content-wrapper {
    position: relative;
    min-height: 200px;
}

/* Initial selection two-column layout */
.initial-selection-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
}

@media (min-width: 768px) {
    .initial-selection-layout {
        grid-template-columns: 2fr 1fr;
        gap: 20px;
    }
}

.selection-products-section {
    min-height: 0; /* Allow flex shrinking */
}

.selection-actions-section {
    display: flex;
    flex-direction: column;
}

/* Hide inline actions on mobile, show on tablet */
.selection-actions-inline {
    display: none;
}

@media (min-width: 768px) {
    .selection-actions-inline {
        display: block;
        position: sticky;
        top: 0;
    }
}

/* Hide mobile actions on tablet */
.initial-actions-mobile {
    display: flex;
}

@media (min-width: 768px) {
    .initial-actions-mobile {
        display: none;
    }
}

.slot-chip {
    font-weight: 500;
}

/* Compact Product Grid for tablets */
.product-grid-compact {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
    max-height: 450px;
    overflow-y: auto;
    padding: 4px;
}

@media (min-width: 768px) and (max-width: 1024px) {
    .product-grid-compact {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
        max-height: 500px;
    }
}

.product-item-compact {
    border: 2px solid #e0e0e0;
    border-radius: 6px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    overflow: hidden;
    position: relative;
    display: flex;
    flex-direction: column;
}

.product-item-compact:hover {
    border-color: #FFC107;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}

.product-item-compact.selected {
    border-color: #4CAF50;
    background-color: rgba(76, 175, 80, 0.05);
    box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.product-item-compact.paired-item {
    border-color: #FFC107;
    background-color: rgba(255, 193, 7, 0.05);
}

.product-item-compact.paired-item.selected {
    border-color: #4CAF50;
    background-color: rgba(76, 175, 80, 0.05);
}

.product-image-container-compact {
    width: 100%;
    height: 120px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.product-info-compact {
    padding: 8px;
    min-height: 60px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.product-sku-compact {
    font-size: 11px;
    font-weight: 600;
    color: #333;
    word-break: break-word;
    line-height: 1.2;
}

.product-checkbox-compact {
    position: absolute;
    top: 6px;
    right: 6px;
    background-color: rgba(255, 255, 255, 0.95);
    border-radius: 50%;
    padding: 2px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* Product Grid Styles (original - for mobile fallback) */
.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    max-height: 500px;
    overflow-y: auto;
    padding: 4px;
}

.product-item {
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    background-color: white;
    cursor: pointer;
    transition: all 0.2s ease;
    overflow: hidden;
    position: relative;
}

.product-item:hover {
    border-color: #FFC107;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
}

.product-item.selected {
    border-color: #4CAF50;
    background-color: rgba(76, 175, 80, 0.05);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
}

.product-item.paired-item {
    border-color: #FFC107;
    background-color: rgba(255, 193, 7, 0.05);
}

.product-item.paired-item.selected {
    border-color: #4CAF50;
    background-color: rgba(76, 175, 80, 0.05);
}

.product-image-container {
    width: 100%;
    height: 200px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
}

.product-image {
    width: 100%;
    height: 100%;
}

.product-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #fafafa;
}

.product-info {
    padding: 12px;
    min-height: 80px;
}

.product-sku {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 4px;
    word-break: break-word;
}

.product-checkbox {
    position: absolute;
    top: 8px;
    right: 8px;
    background-color: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    padding: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.item-list {
    background-color: white;
    border: 1px solid rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
}

.existing-items-list {
    background-color: #f5f5f5;
    border: 1px solid rgba(0, 0, 0, 0.1);
    max-height: 250px;
    overflow-y: auto;
}

.existing-item {
    background-color: white;
    margin-bottom: 4px;
    border-radius: 4px;
}

.item-list-item {
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    cursor: pointer;
}

.item-list-item:hover {
    background-color: rgba(0, 0, 0, 0.03);
}

.item-list-item:last-child {
    border-bottom: none;
}

.selected-item {
    background-color: rgba(76, 175, 80, 0.08) !important;
    border-left: 4px solid #4CAF50;
}

.item-checkbox {
    pointer-events: none;
}

.other-items-select {
    background-color: white;
}

.selection-summary {
    display: flex;
    align-items: center;
}

.v-btn {
    text-transform: none;
    font-weight: 500;
}

.v-alert {
    border-radius: 8px;
}

.v-chip {
    font-weight: 500;
}

/* Custom scrollbar for product grids and item list */
.product-grid::-webkit-scrollbar,
.product-grid-compact::-webkit-scrollbar,
.item-list::-webkit-scrollbar {
    width: 6px;
}

.product-grid::-webkit-scrollbar-track,
.product-grid-compact::-webkit-scrollbar-track,
.item-list::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
}

.product-grid::-webkit-scrollbar-thumb,
.product-grid-compact::-webkit-scrollbar-thumb,
.item-list::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
}

.product-grid::-webkit-scrollbar-thumb:hover,
.product-grid-compact::-webkit-scrollbar-thumb:hover,
.item-list::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
}

/* Responsive adjustments */
@media (max-width: 767px) {
    .product-grid-compact {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 8px;
        max-height: 400px;
    }

    .product-image-container-compact {
        height: 100px;
    }

    .product-grid {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
        gap: 12px;
        max-height: 400px;
    }

    .product-image-container {
        height: 150px;
    }

    .item-list {
        max-height: 250px;
    }
}

@media (max-width: 480px) {
    .product-grid-compact {
        grid-template-columns: repeat(2, 1fr);
        gap: 8px;
    }

    .product-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }
}

/* Selection animation */
.selected-item .v-list-item__content {
    transform: translateX(4px);
}

.item-list-item .v-list-item__content {
    transition: transform 0.2s ease;
}

/* Wizard mode styles */
.scanning-mode {
    min-height: 200px;
}

/* Wizard layout - two columns on tablet */
.wizard-layout {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

/* Tablet and up: two columns */
@media (min-width: 768px) {
    .wizard-layout {
        grid-template-columns: 1fr 1fr;
        gap: 12px;
    }
}

.wizard-image-section,
.wizard-info-section {
    display: flex;
    flex-direction: column;
    min-width: 0; /* Allow shrinking below content size */
}

.image-card,
.info-card {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 0; /* Allow shrinking */
}

.info-card {
    min-height: 280px;
}

@media (min-width: 768px) and (max-width: 1024px) {
    .info-card {
        min-height: 300px;
    }
}

.info-card-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100%;
    padding: 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e0e0e0;
    min-width: 0; /* Allow shrinking */
    overflow: hidden; /* Prevent overflow */
}

.wizard-actions-inline {
    margin-top: auto;
    padding-top: 8px;
    min-width: 0; /* Allow button to shrink */
}

.wizard-actions-inline .v-btn {
    min-width: 0; /* Allow button content to wrap if needed */
    word-break: break-word;
}

/* Hide inline actions on mobile, show bottom actions */
.wizard-actions-mobile {
    display: block;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
}

@media (min-width: 768px) {
    .wizard-actions-mobile {
        display: none;
    }
}

@media (max-width: 767px) {
    .wizard-actions-inline {
        display: none;
    }
}

.wizard-image-container {
    width: 100%;
    height: 280px;
    background-color: #fafafa;
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e0e0e0;
}

/* Tablet optimization */
@media (min-width: 768px) and (max-width: 1024px) {
    .wizard-image-container {
        height: 300px;
    }
}

.wizard-product-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.wizard-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #f5f5f5 0%, #fafafa 100%);
}

/* Responsive adjustments for wizard */
@media (max-width: 767px) {
    .wizard-image-container {
        height: 220px;
    }

    .product-grid {
        grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        gap: 12px;
    }

    .modal-content-wrapper {
        padding: 12px !important;
    }

    .info-card {
        min-height: auto !important;
    }
}

@media (max-width: 480px) {
    .wizard-image-container {
        height: 180px;
    }

    .product-grid {
        grid-template-columns: 1fr;
        gap: 8px;
    }
}
</style>